// BL-173 Gates 1–2 — Stripe Customer creation + SetupIntent card-save capture.
// Spec: IntelAgent_BL173_Payments_Handling_Collection_v1_00.md (§4 Gate 1, §5 Gate 2, §6 Gate 3).
//
// Pure, fetch-injected helpers so the same logic is unit-testable here and importable
// by the stripe-setup-intent Edge Function (same pattern as payoutSplit.js /
// stripe-payout-split). A raw card number never reaches this module: Stripe.js
// tokenizes client-side and only the resulting PaymentMethod id is handled here.

export const STRIPE_API_BASE_URL = 'https://api.stripe.com';

export const USER_CARD_LABELS = ['personal', 'business'];
export const ACCOUNT_CARD_LABEL = 'company';

function appendFormValue(params, key, value) {
  if (value === undefined || value === null || value === '') return;
  if (typeof value === 'object') {
    Object.entries(value).forEach(([childKey, childValue]) => {
      appendFormValue(params, `${key}[${childKey}]`, childValue);
    });
    return;
  }
  params.append(key, String(value));
}

export function encodeStripeForm(payload = {}) {
  const params = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => appendFormValue(params, key, value));
  return params.toString();
}

async function readResponseText(response) {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

export async function stripeRequest({
  fetchImpl,
  secretKey,
  path,
  method = 'POST',
  payload = null,
  // BL-173 Gate 4: Stripe replays the original response for a repeated
  // Idempotency-Key, so a retried charge cannot become a second charge.
  idempotencyKey = null,
  apiBaseUrl = STRIPE_API_BASE_URL,
}) {
  if (!secretKey) {
    throw new Error('Stripe secret key is required for card capture.');
  }
  if (typeof fetchImpl !== 'function') {
    throw new Error('A fetch implementation is required for Stripe requests.');
  }

  const headers = { Authorization: ['Bearer', secretKey].join(' ') };
  if (idempotencyKey) {
    headers['Idempotency-Key'] = String(idempotencyKey);
  }
  const options = { method, headers };

  if (method !== 'GET' && payload) {
    headers['Content-Type'] = 'application/x-www-form-urlencoded';
    options.body = encodeStripeForm(payload);
  }

  const response = await fetchImpl(`${apiBaseUrl}${path}`, options);

  if (!response.ok) {
    const detail = await readResponseText(response);
    throw new Error(`Stripe request failed (${response.status}): ${detail || 'No response body returned.'}`);
  }

  return response.json();
}

export async function createStripeCustomer({
  fetchImpl,
  secretKey,
  email = null,
  name = null,
  metadata = {},
  apiBaseUrl = STRIPE_API_BASE_URL,
}) {
  const customer = await stripeRequest({
    fetchImpl,
    secretKey,
    path: '/v1/customers',
    payload: {
      ...(email ? { email } : {}),
      ...(name ? { name } : {}),
      metadata,
    },
    apiBaseUrl,
  });

  if (!customer?.id) {
    throw new Error('Stripe did not return a Customer id.');
  }

  return customer.id;
}

// Gate 1 — get-or-create. `lookupExistingCustomerId` returns the stripe_customer_id
// already recorded for this owner (or null); only when there is none do we spend a
// Stripe API call creating one.
export async function resolveStripeCustomerId({
  lookupExistingCustomerId,
  fetchImpl,
  secretKey,
  email = null,
  name = null,
  metadata = {},
  apiBaseUrl = STRIPE_API_BASE_URL,
}) {
  const existing = await lookupExistingCustomerId();
  if (existing) {
    return { customerId: existing, created: false };
  }

  const customerId = await createStripeCustomer({
    fetchImpl,
    secretKey,
    email,
    name,
    metadata,
    apiBaseUrl,
  });

  return { customerId, created: true };
}

// Gate 2 — SetupIntent: saves a card with no charge.
export async function createSetupIntent({
  fetchImpl,
  secretKey,
  customerId,
  metadata = {},
  apiBaseUrl = STRIPE_API_BASE_URL,
}) {
  if (!customerId) {
    throw new Error('A Stripe Customer id is required to create a SetupIntent.');
  }

  const intent = await stripeRequest({
    fetchImpl,
    secretKey,
    path: '/v1/setup_intents',
    payload: {
      customer: customerId,
      usage: 'off_session',
      'payment_method_types[0]': 'card',
      metadata,
    },
    apiBaseUrl,
  });

  if (!intent?.client_secret) {
    throw new Error('Stripe did not return a SetupIntent client_secret.');
  }

  return { id: intent.id ?? null, clientSecret: intent.client_secret, status: intent.status ?? null };
}

export async function retrieveStripePaymentMethod({
  fetchImpl,
  secretKey,
  paymentMethodId,
  apiBaseUrl = STRIPE_API_BASE_URL,
}) {
  if (!paymentMethodId) {
    throw new Error('A Stripe PaymentMethod id is required.');
  }

  return stripeRequest({
    fetchImpl,
    secretKey,
    path: `/v1/payment_methods/${encodeURIComponent(paymentMethodId)}`,
    method: 'GET',
    apiBaseUrl,
  });
}

// Gate 3b — removing a card. Detaching at Stripe is what actually stops the token being
// usable; deleting our row alone would leave a live PaymentMethod on the Customer.
export async function detachStripePaymentMethod({
  fetchImpl,
  secretKey,
  paymentMethodId,
  apiBaseUrl = STRIPE_API_BASE_URL,
}) {
  if (!paymentMethodId) {
    throw new Error('A Stripe PaymentMethod id is required.');
  }

  return stripeRequest({
    fetchImpl,
    secretKey,
    path: `/v1/payment_methods/${encodeURIComponent(paymentMethodId)}/detach`,
    apiBaseUrl,
  });
}

// Owner shape mirrors payment_methods' own CHECK constraint (Gate 3a migration):
// user-owned rows carry portal_account_id and no account_id, account-owned rows
// the reverse. Labels are the user's own declaration, not something Stripe knows.
export function normalizeCardOwner({ ownerType, portalAccountId = null, accountId = null, label }) {
  const normalizedOwnerType = String(ownerType ?? '').trim();
  const normalizedLabel = String(label ?? '').trim().toLowerCase();

  if (normalizedOwnerType === 'user') {
    if (!portalAccountId) {
      throw new Error('portalAccountId is required for a user-owned card.');
    }
    if (!USER_CARD_LABELS.includes(normalizedLabel)) {
      throw new Error(`label must be one of: ${USER_CARD_LABELS.join(', ')}.`);
    }
    return { ownerType: 'user', portalAccountId, accountId: null, label: normalizedLabel };
  }

  if (normalizedOwnerType === 'account') {
    if (!accountId) {
      throw new Error('accountId is required for an account-owned (company) card.');
    }
    if (normalizedLabel !== ACCOUNT_CARD_LABEL) {
      throw new Error(`label must be "${ACCOUNT_CARD_LABEL}" for an account-owned card.`);
    }
    return { ownerType: 'account', portalAccountId: null, accountId, label: ACCOUNT_CARD_LABEL };
  }

  throw new Error('ownerType must be "user" or "account".');
}

function toSmallInt(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

// Brand / last4 / expiry are display-only values read back from Stripe's own
// PaymentMethod object — never from anything the browser sent us.
export function buildPaymentMethodRow({
  orgId,
  owner,
  stripeCustomerId,
  paymentMethod,
  isDefault = false,
  addedBy = null,
}) {
  if (!orgId) {
    throw new Error('orgId is required to persist a payment method.');
  }
  if (!stripeCustomerId) {
    throw new Error('stripeCustomerId is required to persist a payment method.');
  }
  if (!paymentMethod?.id) {
    throw new Error('A Stripe PaymentMethod is required to persist a payment method.');
  }

  const card = paymentMethod.card ?? {};

  return {
    org_id: orgId,
    owner_type: owner.ownerType,
    portal_account_id: owner.portalAccountId,
    account_id: owner.accountId,
    stripe_customer_id: stripeCustomerId,
    stripe_payment_method_id: paymentMethod.id,
    label: owner.label,
    brand: card.brand ?? null,
    last4: card.last4 ?? null,
    exp_month: toSmallInt(card.exp_month),
    exp_year: toSmallInt(card.exp_year),
    is_default: Boolean(isDefault),
    added_by: addedBy,
  };
}

// The saved PaymentMethod must actually be attached to the Customer we issued the
// SetupIntent for — otherwise a caller could name any PaymentMethod id it liked.
export function assertPaymentMethodBelongsToCustomer(paymentMethod, customerId) {
  if (!paymentMethod?.customer || paymentMethod.customer !== customerId) {
    throw new Error('This payment method is not attached to the expected Stripe Customer.');
  }
  if (paymentMethod.type && paymentMethod.type !== 'card') {
    throw new Error('Only card payment methods are supported.');
  }
  return true;
}

// --- Gate 3b — display helpers for the card list -------------------------------------
// The caps here mirror the Gate 3a trigger (enforce_payment_method_caps); the database
// stays the enforcer, these only let the UI say "3 of 4 used" before an add is attempted.
export const USER_CARD_CAP = 4;
export const ACCOUNT_CARD_CAP = 1;

const CARD_BRAND_NAMES = {
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'Eftpos Australia',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

export function formatCardBrand(brand) {
  const key = String(brand ?? '').trim().toLowerCase();
  if (!key) return 'Card';
  return CARD_BRAND_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export function formatCardExpiry(expMonth, expYear) {
  const month = Number(expMonth);
  const year = Number(expYear);
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(year)) {
    return '';
  }
  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
}

// Expiry is a display concern only — Stripe is the authority on whether a card can be
// charged. This just lets the list warn before a charge fails.
export function isCardExpired(card, now = new Date()) {
  const month = Number(card?.exp_month);
  const year = Number(card?.exp_year);
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(year)) {
    return false;
  }
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth() + 1;
  return year < currentYear || (year === currentYear && month < currentMonth);
}

export function describeCard(card) {
  const brand = formatCardBrand(card?.brand);
  const last4 = card?.last4 ? `•••• ${card.last4}` : '•••• ••••';
  const expiry = formatCardExpiry(card?.exp_month, card?.exp_year);
  return expiry ? `${brand} ${last4} · exp ${expiry}` : `${brand} ${last4}`;
}

// Default first, then newest — the order a person expects to read their own wallet in.
export function sortCards(cards = []) {
  return [...cards].sort((a, b) => {
    if (Boolean(a?.is_default) !== Boolean(b?.is_default)) {
      return a?.is_default ? -1 : 1;
    }
    return String(b?.created_at ?? '').localeCompare(String(a?.created_at ?? ''));
  });
}
